import type { Vec3 } from './assemblyPose';

type Three = typeof import('three');

// HOW FAR A SCREW ADVANCES IN ONE TURN, MEASURED ON ITS OWN 3D MODEL — so that turned at this
// rate, its drawn thread stays where it bites into the wood instead of sliding through it. Read
// off the model rather than a table by size: a table is right about the screw in the box and can
// be wrong about the one drawn, and it is the drawn one that is watched turning.
//
// HOW: at eight angles round the axis, a ray fired in towards it every few hundredths of a
// millimetre along its length gives the radius profile there. A thread is a row of crests; their
// spacing at one angle is the PITCH. A helix also moves its crests along as the angle turns — by a
// whole lead per turn — so how far the crests shift from one angle to the next says how many
// starts the thread has (an eighth of the spacing per 45° for one, a quarter for two), and the
// LEAD — the advance per turn — is the pitch times that.
//
// Null where no thread is read: too few crests, crests not evenly spaced, or crests that do not
// move with the angle (rings, not a helix). The caller says so; nothing is guessed in its place.

const ANGLES = 8;
// The sampling step along the axis, in mm, and its bounds in samples.
const STEP_MM = 0.04;
const MIN_SAMPLES = 200;
const MAX_SAMPLES = 2500;
// A crest stands at least this far (mm) over the lower of the troughs either side of it.
const PROMINENCE_MM = 0.08;
// Spacings this close (fraction) to their median are the thread's; the rest are its ends.
const SPACING_TOL = 0.2;
const MIN_CRESTS = 4;

export async function threadLeadOf(THREE: Three, obj: import('three').Object3D, axis: { into: Vec3; len: number }): Promise<number | null> {
  const { MeshBVH } = await import('three-mesh-bvh');
  obj.updateMatrixWorld(true);
  const ax = axis.into.findIndex((c) => c !== 0);
  if (ax < 0) return null;
  const [a1, a2] = [0, 1, 2].filter((c) => c !== ax);
  const box = new THREE.Box3().setFromObject(obj);
  const lo = box.min.getComponent(ax);
  const hi = box.max.getComponent(ax);
  const across = Math.max(
    Math.abs(box.min.getComponent(a1)), Math.abs(box.max.getComponent(a1)),
    Math.abs(box.min.getComponent(a2)), Math.abs(box.max.getComponent(a2)),
  );
  const far = across * 1.5 + 1;

  // ITS OWN TREES, INDIRECT: the geometry is shared with the drawn copies, and an ordinary tree
  // reorders the index it is built on.
  const meshes: { bvh: import('three-mesh-bvh').MeshBVH; inv: import('three').Matrix4; m: import('three').Matrix4 }[] = [];
  obj.traverse((o) => {
    const mesh = o as import('three').Mesh;
    if (!mesh.isMesh || !mesh.geometry?.getAttribute('position')) return;
    meshes.push({
      bvh: new MeshBVH(mesh.geometry, { indirect: true }),
      inv: mesh.matrixWorld.clone().invert(),
      m: mesh.matrixWorld,
    });
  });
  if (!meshes.length) return null;

  const ray = new THREE.Ray();
  const from = new THREE.Vector3();
  const dir = new THREE.Vector3();
  const hitAt = new THREE.Vector3();
  // How far from the axis the surface stands at `s` along it, seen from angle `th`. NaN: nothing there.
  const radius = (s: number, th: number) => {
    const c = Math.cos(th);
    const sn = Math.sin(th);
    from.set(0, 0, 0).setComponent(ax, s).setComponent(a1, c * far).setComponent(a2, sn * far);
    dir.set(0, 0, 0).setComponent(a1, -c).setComponent(a2, -sn);
    let best = Infinity;
    for (const { bvh, inv, m } of meshes) {
      ray.origin.copy(from).applyMatrix4(inv);
      ray.direction.copy(from).add(dir).applyMatrix4(inv).sub(ray.origin).normalize();
      const hit = bvh.raycastFirst(ray, THREE.DoubleSide);
      if (!hit) continue;
      hitAt.copy(hit.point).applyMatrix4(m);
      best = Math.min(best, hitAt.distanceTo(from));
    }
    return best === Infinity ? NaN : far - best;
  };

  const n = Math.min(MAX_SAMPLES, Math.max(MIN_SAMPLES, Math.round((hi - lo) / STEP_MM)));
  const ds = (hi - lo) / (n - 1);
  const crestsAt: number[][] = [];
  for (let k = 0; k < ANGLES; k += 1) {
    const th = (k / ANGLES) * 2 * Math.PI;
    const r = Array.from({ length: n }, (_, i) => radius(lo + i * ds, th));
    crestsAt.push(crestsOf(r, ds).map((i) => lo + i * ds));
  }

  // THE PITCH: the median spacing of neighbouring crests, over every angle.
  const gaps = crestsAt.flatMap((c) => c.slice(1).map((x, i) => x - c[i]));
  if (gaps.length < MIN_CRESTS) return null;
  const pitch = median(gaps);
  const even = gaps.filter((g) => Math.abs(g - pitch) <= SPACING_TOL * pitch);
  if (even.length < Math.max(MIN_CRESTS, gaps.length * 0.5)) return null;
  const p = median(even);

  // THE STARTS: how far, as a share of the pitch, each crest's nearest neighbour one angle on lies.
  const shifts: number[] = [];
  for (let k = 0; k < ANGLES; k += 1) {
    const here = crestsAt[k];
    const next = crestsAt[(k + 1) % ANGLES];
    for (const c of here) {
      let bestD = Infinity;
      for (const x of next) {
        const d = x - c;
        if (Math.abs(d) <= p / 2 && Math.abs(d) < Math.abs(bestD)) bestD = d;
      }
      if (bestD !== Infinity) shifts.push(Math.abs(bestD) / p);
    }
  }
  if (shifts.length < MIN_CRESTS) return null;
  const starts = Math.round(median(shifts) * ANGLES);
  if (starts < 1 || starts > 3) return null;
  return Math.round(p * starts * 100) / 100;
}

// The crests of a radius profile, by index: local highs standing PROMINENCE_MM over the higher of
// the two lowest points either side of them before a higher point is met.
function crestsOf(r: number[], ds: number): number[] {
  const out: number[] = [];
  const reach = Math.round(5 / ds);
  for (let i = 1; i < r.length - 1; i += 1) {
    const v = r[i];
    if (!(v >= r[i - 1] && v > r[i + 1])) continue;
    const lowest = (step: number) => {
      let low = v;
      for (let j = i + step, t = 0; j >= 0 && j < r.length && t < reach; j += step, t += 1) {
        if (Number.isNaN(r[j])) break;
        if (r[j] > v) return low;
        low = Math.min(low, r[j]);
      }
      return low;
    };
    if (v - Math.max(lowest(-1), lowest(1)) >= PROMINENCE_MM) out.push(i);
  }
  return out;
}

function median(xs: number[]): number {
  const s = [...xs].sort((a, b) => a - b);
  const m = s.length >> 1;
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}
